import $ from 'jquery';
import _ from 'underscore';
import editorTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/adCustomizersCountdownEditor.pug';
import editorTemplateWithAdCustomizer from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/editorTemplateWithAdCustomizer.pug';
import { getSupportedLanguages, getLanguageCode } from '@bingads-webui-campaign/ad-customizers';
import Backbone from 'backbone';
import SimpleForm from '@bingads-webui-component-legacy/form/view/simple-form';
import kendo from '@bingads-webui/kendo/kendo.datepicker';
import datePickerTemplate from '@bingads-webui-campaign-legacy/crud-ad/template/adsEditor/adCustomizersCountdownDatePicker.pug';
import config from '@bingads-webui-component-legacy/config/index';
import timeZoneMapper from '@bingads-webui-component-legacy/timezonemapper/index';

const i18n = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-customizers');
const i18nAdCustomizers = window.globalLegacyI18n.get('@bingads-webui/i18n-amd-loader!campaignui/feature/ad-customizers');

/**
 * Date picker view that is specialized for countdown editor
 */
class DatePicker extends Backbone.View {
  initialize( {observable} ) {
    this.observable = observable;
  }

  render() {
    this.$el.html(datePickerTemplate());
    let parameters = {
      culture: config.get('culture') || 'en-US',
      format: 'yyyy/MM/dd',
      min: new Date(),
      animation: false,
      footer: false,
      value: this.observable(),
    };

    let elementDatePicker = this.$('#countdowndatepicker');

    elementDatePicker.kendoDatePicker(parameters);

    let datePicker = elementDatePicker.data('kendoDatePicker');

    elementDatePicker.click(() => {
      datePicker.open();
    });

    datePicker.bind('change', () => {
      let value = kendo.toString(datePicker.value(), parameters.format);

      this.observable(value);
    });

    elementDatePicker.addClass('form-control');

    return this;
  }
}

/**
 * Countdown editor form schema
 */
const schema = {
  name: 'Countdown',
  type: 'object',
  properties: {
    date: {
      type: 'string',
      pattern: '^[0-9]{4}/([1-9]|0[1-9]|1[0-2])/([1-9]|0[1-9]|[1-2][0-9]|3[0-1])$',
    },
    time: {
      type: 'string',
      pattern: '^(?:((?:[0-1][0-9]|2[0-3]):(?:[0-5][0-9]):(?:[0-5][0-9]))|)$',
    },
    days: {
      type: 'integer',
      minimum: 1,
      maximum: 999,
    },
    isGlobal: {
      type: 'boolean',
    },
    language: {
      type: 'string',
      enum: getSupportedLanguages(i18nAdCustomizers, config),
    },
  },
  required: ['date', 'days', 'language'],
};

/**
 * Countdown editor form fields
 */
const fields = [
  {
    property: 'date',
    placeholder: 'yyyy/MM/dd',
    label: i18n.getString('CountdownEditor_CountdownEnds_Label'),
    control: DatePicker,
    validators: {
      pattern: {
        message: i18n.getString('CountdownEditor_CountdownEnds_PatternValidationMessage'),
      },
      required: {
        message: i18n.getString('CountdownEditor_CountdownEnds_RequiredValidationMessage'),
      },
    },
  },
  {
    property: 'time',
    placeholder: 'HH:mm:ss',
    label: i18n.getString('CountdownEditor_CountdownEndsTime_Label'),
    showLabel: false,
    validators: {
      pattern: {
        message: i18n.getString('CountdownEditor_CountdownEndsTime_PatternValidationMessage'),
      },
    },
  },
  {
    property: 'days',
    label: i18n.getString('CountdownEditor_CountdownStarts_Label'),
    validators: {
      type: {
        message: i18n.getString('CountdownEditor_CountdownStarts_TypeValidationMessage'),
      },
      range: {
        message: i18n.getString('CountdownEditor_CountdownStarts_RangeValidationMessage'),
      },
      required: {
        message: i18n.getString('CountdownEditor_CountdownStarts_RequiredValidationMessage'),
      },
    },
  },
  {
    property: 'isGlobal',
    control: 'radios',
    valueMessages: {
      true: `${i18n.getString('CountdownEditor_AccountTimeZone_Label')} ${timeZoneMapper.getTimeZoneText(config.get('AccountTimeZone'))}`,
      false: i18n.getString('CountdownEditor_AdViewersTimeZone_Label'),
    },
    label: i18n.getString('CountdownEditor_TimeZone_Label'),
  },
  {
    property: 'language',
    label: i18n.getString('CountdownEditor_Language_Label'),
  },
];

/**
 * Wraps template function to use localization and syntax info
 * @param {function} template - editor template function
 * @param {string} syntax - text for header of the editor where we display valid syntax
 * @returns {function} template function
 */
function prepareTemplate(template, syntax) {
  return _.wrap(template, (func, params) => func(_.defaults(params, {i18n, syntax})));
}

/**
 * Creates a string that contains a countdown definition
 * @param {string} prefix - text to be concatenated in front of countdown definition (optional)
 * @param {string} type - countdown type
 * @param {string} date - countdown end date (optional, defaults to 'yyyy/MM/dd')
 * @param {string} time - countdown end time (optional, defaults to 'HH:mm:ss')
 * @param {string} language - countdown language
 * @param {string} days - duration of countdown in days
 * @param {string} suffix - text to be concatenated at the end of countdown definition (optional)
 * @returns {string} formatted string
 */
function formatCountdown(prefix, type, date, time, language, days, suffix) {
  let ret = '{prefix}{={type}("{date} {time}", "{language}", {days})}{suffix}';

  ret = ret.replace('{prefix}', prefix ? prefix : '');
  ret = ret.replace('{type}', type);
  ret = ret.replace('{date}', date ? date : 'yyyy/MM/dd');
  ret = ret.replace('{time}', time ? time : 'HH:mm:ss');
  ret = ret.replace('{language}', language);
  ret = ret.replace('{days}', days);
  ret = ret.replace('{suffix}', suffix ? suffix : '');

  return ret;
}

/**
 * Countdown editor view
 */
class CountdownEditor extends Backbone.View {
  initialize({
    target,
    options}) {

    this.target = target;

    const model = {
      date: options.date,
      time: options.time,
      days: options.days,
      isGlobal: options.type === 'global_countdown',
      language: options.language.displayName,
    };

    this.includeAdCustomizerFeed = options.includeAdCustomizerFeed;

    this.syntax = formatCountdown(
      `${i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label')} `,
      options.type,
      null,
      null,
      i18n.getString('CountdownEditor_Syntax_Language_Label'),
      i18n.getString('CountdownEditor_Syntax_DaysBefore_Label'),
      null);

    if (!this.includeAdCustomizerFeed) {
      this.form = new SimpleForm({
        schema,
        fields,
        model,
        template: prepareTemplate(editorTemplate, this.syntax),
        saveButtonText: i18n.getString('AdCustomizers_Editor_Set_Button'),
      });

      this.form.on('save', (newModel) => {
        let prefix = target.value.substring(0, options.startIndex);
        let suffix = target.value.substring(options.endIndex);

        let newValue = formatCountdown(
          prefix,
          newModel.isGlobal ? 'global_countdown' : 'countdown',
          newModel.date,
          newModel.time.length === 0 ? '00:00:00' : newModel.time,
          getLanguageCode(newModel.language, i18nAdCustomizers, config),
          newModel.days,
          suffix);

        let cursorPosition = newValue.length - suffix.length;

        options.updateAction(newValue);
        target.setSelectionRange(cursorPosition, cursorPosition);
        target.focus();

        hide(); // eslint-disable-line no-use-before-define
      });

      this.form.on('cancel', () => hide()); // eslint-disable-line no-use-before-define
    }
    this.$el.attr('role', 'dialog');

    return this;
  }

  render() {
    let $target = $(this.target);
    let position = $target.offset();

    position.top = position.top + $target.outerHeight();

    this.$el.css({
      position: 'absolute',
      visibility: 'visible',
      left: position.left,
      top: position.top,
    });

    if (this.includeAdCustomizerFeed) {
      this.$el.html(editorTemplateWithAdCustomizer({syntax: this.syntax, i18n}));
    } else {
      this.$el.append(this.form.render().el);
    }

    // Clicking on elements having 'ac-helper-area' class won't hide currently visible helper
    this.$el.addClass('ac-helper-area adCustomizersEditor');

    return this;
  }
}

/*
 * Editor instance (used by hide() and show() to update, register/deregister events and change visibility)
 */
let editor = null;

/**
 * Hides countdown editor when user clicks outside of ad customizer helper areas and date picker areas
 * @param {object} event - click event
 * @returns {undefined}
 */
function clickedAway(event) {
  let target = $(event.target);

  if (target.closest('.ac-helper-area').length === 0 &&
    target.closest('#countdowndatepicker_dateview').length === 0 &&
    target.closest('.k-calendar-container').length === 0 &&
    target.closest('.k-content').length === 0) {
    hide(); // eslint-disable-line no-use-before-define
  }
}

/**
 * Handles key down events.
 * Hides editor when user hits escape.
 * @param {object} event - click event
 * @returns {undefined}
 */
function keydown(event) {
  switch (event.key) {
  case 'Escape':
    hide(); // eslint-disable-line no-use-before-define
    break;
  default:
    break;
  }
}

/**
 * Hides countdown editor and deregisters clickaway event.
 * @returns {undefined}
 */
export function hide() {
  if (editor) {
    $(editor.target).off('keydown', keydown);
    $(window).off('click', clickedAway);
    editor.remove();
    editor = null;
  }
}

/**
 * Shows countdown editor and registers to global click events.
 * @param {object} target - element the editor will be attached to.
 * @param {object} options - editor model and cursor position info.
 * @returns {undefined}
 */
export function show(target, options) {
  hide();

  editor = new CountdownEditor({ target, options });
  $('body').append(editor.render().el);
  $(window).on('click', clickedAway);
  $(target).on('keydown', keydown);
}

/**
 * Checks whether countdown editor is visible
 * @returns {bool} true if visible
 */
export function isVisible() {
  return editor !== null;
}
